import { useEffect, useRef, useState } from "react";
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import { RadioButton } from "primereact/radiobutton";
import { Skeleton } from "primereact/skeleton";
import { useToast } from "sj-ui-toolkit/src/hooks";
import { humanDateWithoutTime } from "sj-ui-toolkit/src/utils/dates";
import { C, themeClasses } from "sj-ui-toolkit/src/atlas/utils";
import AssessmentDateMode from "@/components/assignments/AssessmentDateMode";
import { loadAssignmentPolicyByIdAsync, updateAssignmentPolicyAsync } from "@/api/assignments";
import type { CreateAssignmentPayload, FormSettings } from "@/api/assignments";
import {
  buildAssignmentPolicyPayload,
  normalizeNotificationsFrequency,
  parseAssignmentDueDate,
  reminderTextMap,
  capitalize,
} from "@/helpers/assignmentHelpers";

// ─── Date helpers (mirrors AssignmentSettings) ──────────────────────────────

const findDaysBetween = (a: Date, b: Date): number => {
  const msPerDay = 1000 * 60 * 60 * 24;
  const start = new Date(a);
  start.setHours(0, 0, 0, 0);
  const end = new Date(b);
  end.setHours(0, 0, 0, 0);
  if (end < start) return 0;
  return Math.round((end.getTime() - start.getTime()) / msPerDay) + 1;
};

const setToEndOfDay = (date: Date | number): Date => {
  const d = new Date(date);
  d.setHours(23, 59, 59, 999);
  return d;
};

// ─── Types ──────────────────────────────────────────────────────────────────

type FieldKey = "name" | "description" | "timeframe" | "reminders";

const notificationOptions = ["none", "daily", "weekly", "monthly", "quarterly"];

// Matches the settings-page card style (elevation-2 surface, rounded, shadow).
const CARD_CLASS = C(themeClasses.bgElevation3, "p-6 rounded-md shadow-md");

interface AssignmentSettingsTabProps {
  assignmentId: string;
  /** Bumping this (e.g. after an external status toggle) re-pulls the loaded settings. */
  reloadKey?: number;
  /** Called after a successful save so the parent can refresh header/overview data. */
  onSaved?: () => void;
}

/** Assignment settings with per-field inline editing — each field's save PUTs the full settings. */
export const AssignmentSettingsTab = ({
  assignmentId,
  reloadKey,
  onSaved,
}: AssignmentSettingsTabProps) => {
  const { showToast } = useToast();

  // showToast identity isn't stable, so keep it out of effect deps via a ref.
  const showToastRef = useRef(showToast);
  showToastRef.current = showToast;

  // Last-saved settings — used to revert a field's unsaved edits on cancel.
  const baselineRef = useRef<FormSettings | null>(null);

  // Fields this tab never edits but the backend requires on every PUT — carried through
  // unchanged from the loaded record (mirrors the edit form's full-payload contract).
  const metaRef = useRef<{ roleId?: string; enrollmentDelay: number }>({ enrollmentDelay: 0 });

  const [values, setValues] = useState<FormSettings | null>(null);
  const [editing, setEditing] = useState<Record<FieldKey, boolean>>({
    name: false,
    description: false,
    timeframe: false,
    reminders: false,
  });
  const [saving, setSaving] = useState<Record<FieldKey, boolean>>({
    name: false,
    description: false,
    timeframe: false,
    reminders: false,
  });

  useEffect(() => {
    let cancelled = false;

    loadAssignmentPolicyByIdAsync(assignmentId)
      .then(policy => {
        if (cancelled) return;
        metaRef.current = {
          roleId: policy.roleId,
          enrollmentDelay: policy.enrollmentDelay ?? 0,
        };
        const settings: FormSettings = {
          name: policy.name,
          description: policy.description,
          // Normalize to an explicit boolean here so downstream code never has to guess
          // what a missing/undefined value means.
          activated: policy.activated ?? false,
          sendReminders: normalizeNotificationsFrequency(policy.notificationsFrequency),
        };
        const dueDate = parseAssignmentDueDate(policy.dueDateAt);
        if (dueDate) {
          settings.endDate = dueDate;
          settings.dates = { dynamic: false };
        } else if (policy.duration && policy.duration > 0) {
          settings.dates = { dynamic: true, dueAfter: { days: policy.duration } };
        }
        baselineRef.current = settings;
        setValues(settings);
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        console.error("Failed to load settings:", error);
        showToastRef.current({ severity: "error", detail: "Failed to load settings" });
      });

    return () => {
      cancelled = true;
    };
  }, [assignmentId, reloadKey]);

  // The subset of settings each field owns — used to revert/commit just that field.
  const fieldSlice = (field: FieldKey, s: FormSettings): Partial<FormSettings> => {
    switch (field) {
      case "name":
        return { name: s.name };
      case "description":
        return { description: s.description };
      case "reminders":
        return { sendReminders: s.sendReminders };
      case "timeframe":
        return { endDate: s.endDate, dates: s.dates };
    }
  };

  const enable = (field: FieldKey) => setEditing(prev => ({ ...prev, [field]: true }));

  const cancel = (field: FieldKey) => {
    const base = baselineRef.current;
    if (base) {
      setValues(prev => (prev ? { ...prev, ...fieldSlice(field, base) } : prev));
    }
    setEditing(prev => ({ ...prev, [field]: false }));
  };

  const buildPayload = (s: FormSettings): Partial<CreateAssignmentPayload> => {
    const isDueDate = !s.dates?.dynamic;
    return buildAssignmentPolicyPayload({
      name: s.name,
      description: s.description,
      // `activated` is normalized to a real boolean at load, so this preserves it as-is
      activated: s.activated ?? false,
      // Dynamic-mode assignments have no fixed due date — null is safe here (confirmed
      // against the backend); `duration` is what actually carries the relative timeframe.
      dueDateAt: isDueDate && s.endDate ? Math.floor(new Date(s.endDate).getTime() / 1000) : null,
      duration: isDueDate ? 0 : (s.dates?.dueAfter?.days ?? 0),
      notificationsFrequency: s.sendReminders,
      // Required by the backend on every update but never edited here — carry through
      // the values from the loaded record unchanged.
      roleId: metaRef.current.roleId,
      enrollmentDelay: metaRef.current.enrollmentDelay,
    });
  };

  const save = async (field: FieldKey) => {
    if (!values) return;
    setSaving(prev => ({ ...prev, [field]: true }));
    try {
      // The backend expects the full settings payload (matches the edit-form's PUT
      // contract) — sending just the changed field's slice 500s server-side.
      await updateAssignmentPolicyAsync(assignmentId, buildPayload(values));
      baselineRef.current = { ...(baselineRef.current ?? {}), ...fieldSlice(field, values) };
      setEditing(prev => ({ ...prev, [field]: false }));
      showToastRef.current({ severity: "success", detail: "Setting saved" });
      onSaved?.();
    } catch (error) {
      console.error("Failed to save setting:", error);
      showToastRef.current({ severity: "error", detail: "Failed to save setting" });
    } finally {
      setSaving(prev => ({ ...prev, [field]: false }));
    }
  };

  if (!values) {
    return (
      <div className="flex flex-col gap-8">
        <div className="grid grid-cols-2 xl:grid-cols-4 gap-x-8 gap-y-8">
          <div className={C(CARD_CLASS, "xl:col-span-2", "flex flex-col gap-2")}>
            <Skeleton width="8rem" height="1.25rem" />
            <Skeleton width="100%" height="2rem" />
          </div>
          {Array.from({ length: 2 }).map((_, i) => (
            <div key={i} className={C(CARD_CLASS, "flex flex-col gap-2")}>
              <Skeleton width="8rem" height="1.25rem" />
              <Skeleton width="100%" height="2rem" />
            </div>
          ))}
        </div>
        <div className={C(CARD_CLASS, "flex flex-col gap-2")}>
          <Skeleton width="10rem" height="1.25rem" />
          <Skeleton width="100%" height="6rem" />
        </div>
      </div>
    );
  }

  // ─── Field header (label + pencil/save/cancel controls) ──────────────────

  const fieldHeader = (field: FieldKey, label: string) => (
    <div
      className={C(
        "flex items-center justify-between border-b-2 pb-3 mb-4",
        themeClasses.borderSurfaceBorder
      )}
    >
      <h2 className="atlas-h4 m-0">{label}</h2>
      <div className="flex items-center gap-1">
        {editing[field] && (
          <Button
            type="button"
            text
            rounded
            severity="secondary"
            icon="pi pi-times"
            loading={saving[field]}
            aria-label={`Cancel editing ${label}`}
            data-testid={`assignment-setting-${field}-cancel`}
            onClick={() => cancel(field)}
          />
        )}
        <Button
          type="button"
          text
          rounded
          icon={editing[field] ? "pi pi-check" : "pi pi-pencil"}
          loading={saving[field]}
          aria-label={editing[field] ? `Save ${label}` : `Edit ${label}`}
          data-testid={`assignment-setting-${field}-toggle`}
          onClick={editing[field] ? () => save(field) : () => enable(field)}
        />
      </div>
    </div>
  );

  // ─── Timeframe helpers / text summary ─────────────────────────────────────

  const endDate = values.endDate ? new Date(values.endDate) : null;
  const isDynamic = values.dates?.dynamic;
  const daysBetween = isDynamic
    ? (values.dates?.dueAfter?.days ?? 0)
    : endDate
      ? findDaysBetween(new Date(), endDate)
      : 0;

  const timeframeText = isDynamic
    ? `Dynamic \u2014 ${daysBetween} day${daysBetween === 1 ? "" : "s"} to complete`
    : endDate
      ? `Fixed date \u2014 ${humanDateWithoutTime(endDate)}`
      : "No due date";

  const handleEndDateChange = (e: { value: Date | null }) => {
    const newEndDate = e.value ? setToEndOfDay(e.value) : undefined;
    const days = newEndDate ? findDaysBetween(new Date(), newEndDate) : 0;
    setValues(prev =>
      prev
        ? {
            ...prev,
            endDate: newEndDate,
            dates: { ...prev.dates, dueAfter: { ...prev.dates?.dueAfter, days } },
          }
        : prev
    );
  };

  const toggleDynamicMode = (dynamic: boolean) => {
    setValues(prev => {
      if (!prev) return prev;
      const days =
        dynamic && !prev.dates?.dueAfter?.days
          ? daysBetween || 0
          : (prev.dates?.dueAfter?.days ?? 0);
      return {
        ...prev,
        dates: {
          ...prev.dates,
          dynamic,
          dueAfter: dynamic ? { ...prev.dates?.dueAfter, days } : prev.dates?.dueAfter,
        },
      };
    });
  };

  const remindersKey = normalizeNotificationsFrequency(values.sendReminders);

  return (
    <div className="flex flex-col gap-8">
      <div className="grid grid-cols-2 xl:grid-cols-4 gap-x-8 gap-y-8">
        {/* Name */}
        <div className={C(CARD_CLASS, "xl:col-span-2")}>
          {fieldHeader("name", "Assignment Name")}
          {editing.name ? (
            <InputText
              id="settings-name"
              data-testid="assignment-setting-name"
              value={values.name ?? ""}
              onChange={e => setValues(prev => (prev ? { ...prev, name: e.target.value } : prev))}
              className="w-full"
              autoFocus
            />
          ) : (
            <p data-testid="assignment-setting-name-text">{values.name || "\u2014"}</p>
          )}
        </div>

        {/* Timeframe */}
        <div className={CARD_CLASS}>
          {fieldHeader("timeframe", "Time Frame")}
          {editing.timeframe ? (
            <AssessmentDateMode
              startDate={new Date()}
              endDate={endDate}
              daysBetween={daysBetween}
              handleEndDateChange={handleEndDateChange}
              toggleDynamicMode={toggleDynamicMode}
              isDynamic={isDynamic}
            />
          ) : (
            <p data-testid="assignment-setting-timeframe-text">{timeframeText}</p>
          )}
        </div>

        {/* Reminders */}
        <div className={CARD_CLASS}>
          {fieldHeader("reminders", "Send Reminders")}
          {editing.reminders ? (
            <div className="flex flex-col gap-2">
              {notificationOptions.map(option => (
                <div key={option} className="flex items-center gap-2">
                  <RadioButton
                    inputId={`settings-reminder-${option}`}
                    name="settingsSendReminders"
                    value={option}
                    checked={remindersKey === option}
                    onChange={() =>
                      setValues(prev => (prev ? { ...prev, sendReminders: option } : prev))
                    }
                  />
                  <label htmlFor={`settings-reminder-${option}`}>{capitalize(option)}</label>
                </div>
              ))}
            </div>
          ) : (
            <div>
              <p data-testid="assignment-setting-reminders-text">{capitalize(remindersKey)}</p>
              <p className="text-secondary body-sm">{reminderTextMap[capitalize(remindersKey)]}</p>
            </div>
          )}
        </div>
      </div>

      {/* Description */}
      <div className={CARD_CLASS}>
        {fieldHeader("description", "Assignment Description")}
        {editing.description ? (
          <InputTextarea
            id="settings-description"
            data-testid="assignment-setting-description"
            value={values.description ?? ""}
            rows={5}
            onChange={e =>
              setValues(prev => (prev ? { ...prev, description: e.target.value } : prev))
            }
            className="w-full"
            autoFocus
          />
        ) : (
          <p data-testid="assignment-setting-description-text">{values.description || "\u2014"}</p>
        )}
      </div>
    </div>
  );
};

export default AssignmentSettingsTab;
